<template>
  <k-modal :show="showModal" @close="closeModal" id="add-ad-hoc-meeting-modal">
    <template #header>
      <h2>Add Ad-Hoc Meeting</h2>
    </template>
    <template #body>
      <ad-hoc-meeting-selection v-if="meetingsReady"
        :meetings="meetings"
        :selected-meeting="selectedMeeting"
        :user-support-function="store.learnerSupportType"
        @update:selected-meeting="selectedMeeting = $event"
      ></ad-hoc-meeting-selection>
      <hr v-if="selectedMeeting && meetingLearner.id" class="horizontal-hr">
      <h3>Select Learner</h3>
      <select-user
          v-model="meetingLearner"
          :show-clear-button="false"
          @reset-meeting-owner="meetingOwner = $event"
      ></select-user>
      <hr v-if="meetingLearner.id" class="horizontal-hr">
      <ad-hoc-programme-selection v-if="meetingLearner.id && programmesReady"
        :programmes="programmes"
        @update:selected-programme-id="selectedProgrammeId = $event"
      ></ad-hoc-programme-selection>
      <hr v-if="selectedProgrammeId" class="horizontal-hr">
      <ad-hoc-meeting-scheduling-section v-if="selectedProgrammeId"
        v-model="meetingOwner"
        :meeting-owner="meetingOwner"
        :line-manager="lineManagerForProgramme"
        :learner-user-id="meetingLearner.id"
        :meeting-id="selectedMeeting?.id"
        @validation="isValidScheduledForm = $event"
        @meeting-owner-selected="getUser($event, 'meetingOwner')"
        @meeting-data="handleMeetingData"
        @reset-meeting-owner="meetingOwner = {}"
      ></ad-hoc-meeting-scheduling-section>
    </template>
    <template #footer>
      <div class="modal-footer-left">
        <button class="btn btn-outlined" @click="closeModal">Cancel</button>
      </div>
      <div class="modal-footer-right">
        <button class="btn btn-success" @click="addMeeting" :disabled="updating || !validForm">
          Add Meeting <i class="fa-duotone fa-spinner fa-spin" v-if="updating" aria-hidden="true"></i>
        </button>
      </div>
    </template>
  </k-modal>
</template>

<style>
#add-ad-hoc-meeting-modal .modal-container {
  width: 80vw;
  max-width: 1080px;
}

#add-ad-hoc-meeting-modal .modal-body {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

#add-ad-hoc-meeting-modal .modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
}

#add-ad-hoc-meeting-modal .user-profile-card {
  margin: 0;
}

#add-ad-hoc-meeting-modal .user-profile-card.flexible-panel {
  flex-direction: row;
}

#add-ad-hoc-meeting-modal .k-user-search-modal .modal-container {
  width: 75vw;
}

#add-ad-hoc-meeting-modal .flexible-panel {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 15px;
  border-radius: 10px;
  border: 1px solid var(--kate-stroke-dark);
  height: stretch;
}

#add-ad-hoc-meeting-modal .support-function {
  color: var(--kate-type-secondary);
  text-transform: capitalize;
  font-family: hk-grotesk-semibold, sans-serif;
}

#add-ad-hoc-meeting-modal h2 {
  margin: 0;
}

#add-ad-hoc-meeting-modal .tags-container,
#add-ad-hoc-meeting-modal section.meeting-attendees,
#add-ad-hoc-meeting-modal section.programme-selection {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 15px;
  margin: 10px 0;
}

#add-ad-hoc-meeting-modal .meeting-owner-body,
#add-ad-hoc-meeting-modal .meeting-attendees-body,
#add-ad-hoc-meeting-modal .programme-selection-body {
  flex: 1;
  display: flex;
  gap: 15px;
  flex-direction: column;
  height: stretch;
}

#add-ad-hoc-meeting-modal .meeting-owner-body {
  margin-bottom: 15px;
}

#add-ad-hoc-meeting-modal .user-selection-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

#add-ad-hoc-meeting-modal .user-selection-actions button.search-button {
  flex: 1;
}

#add-ad-hoc-meeting-modal .user-selection-actions button {
  margin-bottom: 15px;
}

#add-ad-hoc-meeting-modal .tags-container {
  justify-self: start;
  align-self: start;
  margin-bottom: 15px;
}

#add-ad-hoc-meeting-modal .schedule-meeting-heading-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

@media (max-width: 767px) {
  #add-ad-hoc-meeting-modal section.meeting-attendees,
  #add-ad-hoc-meeting-modal section.programme-selection {
    flex-direction: column;
  }

  #add-ad-hoc-meeting-modal section.meeting-attendees div,
  #add-ad-hoc-meeting-modal section.programme-selection div {
    width: 100%;
  }

  #add-ad-hoc-meeting-modal .user-selection-actions {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>

<script>
import TimeMixin from '@mixins/time-mixins';
import KModal from '@base-components/k-modal.vue';
import useGlobalStore from '@stores/global';
import AdHocMeetingSelection from './components/ad-hoc-meeting-selection.vue';
import AdHocProgrammeSelection from './components/ad-hoc-programme-selection.vue';
import AdHocMeetingSchedulingSection from './components/ad-hoc-meeting-scheduling-section.vue';
import SelectUser from './components/select-user.vue';
import isValidLineManager from './utils/line-manager-utils';

export default {
  mixins: [TimeMixin],

  components: {
    SelectUser,
    KModal,
    AdHocMeetingSelection,
    AdHocProgrammeSelection,
    AdHocMeetingSchedulingSection,
  },

  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
  },

  watch: {
    meetingLearner() {
      this.learnerLineManagers = [];
      this.includeLineManager = false;
      if (this.meetingLearner.id) {
        this.getStudentProgrammes();
        this.getLearnerProfile();
      }
    },
    lineManagerForProgramme(newLineManager) {
      if (!newLineManager) {
        this.includeLineManager = false;
      }
    },
  },

  data() {
    return {
      store: useGlobalStore(),
      updating: false,
      // Meeting selection
      meetings: [],
      meetingsReady: false,
      selectedMeeting: undefined,
      // User selection
      meetingLearner: {},
      context: 'learner',
      // Programme selection
      programmes: [],
      programmesReady: false,
      selectedProgrammeId: undefined,
      // Meeting scheduling
      meetingOwner: {},
      meetingDate: undefined,
      startTime: undefined,
      endTime: undefined,
      meetingLink: undefined,
      meetingDescription: undefined,
      scheduleMeeting: false,
      isValidScheduledForm: false,
      includeLineManager: false,
      learnerLineManagers: [],
      teamsMeetingId: null,
      teamsOrganiserUserId: null,
    };
  },

  beforeMount() {
    this.getMeetings();
  },

  computed: {
    validForm() {
      const baseValidation = Boolean(this.meetingLearner?.id && this.selectedMeeting?.id && this.selectedProgrammeId);

      // If scheduling is enabled, all date/time fields and meeting link must be filled
      if (this.scheduleMeeting) {
        return Boolean(baseValidation && this.isValidScheduledForm);
      }

      return baseValidation;
    },
    lineManagerForProgramme() {
      if (!this.selectedProgrammeId || !this.learnerLineManagers.length) {
        return null;
      }
      const lineManager = this.learnerLineManagers.find(
        lm => lm.programme_id === this.selectedProgrammeId,
      );
      return isValidLineManager(lineManager) ? lineManager : null;
    },
  },

  methods: {
    resetForm() {
      this.selectedMeeting = undefined;
      this.meetingLearner = {};
      this.selectedProgrammeId = undefined;
      this.meetingOwner = {};
      this.meetingDate = undefined;
      this.startTime = undefined;
      this.endTime = undefined;
      this.meetingLink = undefined;
      this.meetingDescription = undefined;
      this.scheduleMeeting = false;
      this.includeLineManager = false;
      this.learnerLineManagers = [];
      this.teamsMeetingId = null;
      this.teamsOrganiserUserId = null;
    },

    handleMeetingData(data) {
      this.meetingOwner = data.meetingOwner;
      this.meetingDate = data.meetingDate;
      this.startTime = data.startTime;
      this.endTime = data.endTime;
      this.meetingLink = data.meetingLink;
      this.meetingDescription = data.meetingDescription;
      this.scheduleMeeting = data.scheduleMeeting;
      this.meetingName = data.meetingName;
      this.meetingOwnerId = data.meetingOwnerId;
      this.meetingOwnerRole = data.meetingOwnerRole;
      this.isValidScheduledForm = data.isValidScheduledForm;
      this.includeLineManager = Boolean(data.includeLineManager && this.lineManagerForProgramme);
      this.teamsMeetingId = data.teamsMeetingId;
      this.teamsOrganiserUserId = data.teamsOrganiserUserId;
    },

    scheduleMeetingRequest(moduleMeetingId, moduleId) {
      const logPayload = {
        learnerId: this.meetingLearner.id,
        moduleMeetingId,
        moduleId,
      };
      const meetingOwnerRole = this.meetingOwner?.learner_support_type || 'other';
      const schedulePayload = {
        user_id: this.meetingLearner.id,
        module_meeting_id: moduleMeetingId,
        meeting_link: this.meetingLink,
        description: this.meetingDescription,
        meeting_start: this.formatDate(this.startTime),
        meeting_end: this.formatDate(this.endTime),
        meeting_owner_user_id: this.meetingOwner.id,
        meeting_owner_role: meetingOwnerRole,
        meeting_name: this.selectedMeeting.name,
        include_line_manager: Boolean(this.includeLineManager && this.lineManagerForProgramme),
        ...(this.teamsMeetingId && {
          teams_meeting_id: this.teamsMeetingId,
          teams_organiser_user_id: this.teamsOrganiserUserId,
        }),
      };
      this.$logger.info('Scheduling ad-hoc meeting', logPayload);
      return this.$http.put(`/api/curriculum/admin/module/${moduleId}/meetings/${moduleMeetingId}/user/${this.meetingLearner.id}/schedule`, schedulePayload)
        .then(result => {
          this.$logger.info('Ad-hoc meeting scheduled', logPayload);
          return Promise.resolve({ success: true, result });
        })
        .catch(error => {
          this.$logger.autowarn('Error scheduling ad-hoc meeting', logPayload, error);
          return Promise.resolve({ success: false, error });
        });
    },

    addMeetingRequest() {
      const logPayload = {
        learnerId: this.meetingLearner.id,
        meetingId: this.selectedMeeting.id,
      };
      const meetingPayload = {
        user_id: this.meetingLearner.id,
        meeting_id: this.selectedMeeting.id,
        programme_id: this.selectedProgrammeId,
      };
      this.updating = true;
      this.$logger.info('Adding ad-hoc meeting for learner', logPayload);
      return this.$http.post('/api/curriculum/admin/meeting/ad-hoc', meetingPayload)
        .then(res => {
          this.$logger.info('Ad-hoc meeting added');
          return Promise.resolve({ success: true, moduleMeetingId: res.data.module_meeting_id, moduleId: res.data.module_id });
        }).catch(err => {
          this.$logger.error('Error adding ad-hoc meeting', logPayload, err);
          return Promise.resolve({ success: false, error: err });
        });
    },

    async addMeeting() {
      this.$Loading.minimal();
      const addMeetingResult = await this.addMeetingRequest();
      let success = addMeetingResult.success;
      let error = addMeetingResult.error;
      if (success) {
        // Only schedule the meeting if the user has selected to do so
        if (this.scheduleMeeting) {
          const scheduleMeetingResult = await this.scheduleMeetingRequest(addMeetingResult.moduleMeetingId, addMeetingResult.moduleId);
          success = scheduleMeetingResult.success;
          error = scheduleMeetingResult.error;
        }
        if (success) {
          this.$ktoast.success(`${this.selectedMeeting.name} added for ${this.meetingLearner.full_name}`);
          this.$emit('meeting-added');
          this.closeModal();
        }
      }
      if (!success) {
        this.$ktoast.showError(error);
      }
      this.updating = false;
      this.$Loading.finish();
    },

    closeModal() {
      this.$emit('close');
      this.resetForm();
    },

    // Meeting selection
    getMeetings() {
      this.$logger.info('Getting meetings');
      this.$Loading.start();
      this.meetingsReady = false;
      this.$http.get('/api/curriculum/admin/meeting')
        .then(result => {
          this.meetings = result.data.meetings;
        }).catch(err => {
          if (err.response && err.response.status !== 404) {
            this.$logger.error('Error getting meetings', undefined, err);
            this.$ktoast.showError(err);
          } else {
            this.$logger.warn('No meetings found');
            this.meetings = [];
          }
        }).then(() => {
          this.meetingsReady = true;
          this.$Loading.finish();
        });
    },

    // Programme selection
    getStudentProgrammes() {
      this.$logger.info('Getting student programmes', { studentId: this.meetingLearner.id }, true);
      this.programmesReady = false;
      this.$http.get(`/api/profile/user/${this.meetingLearner.id}/programmes?dashboard=true`)
        .then(result => {
          this.programmes = result.data.programmes;
        }).catch(error => {
          this.$logger.error('Error getting student programmes', { studentId: this.meetingLearner.id }, error);
          this.$ktoast.showError(error);
        }).then(() => {
          this.programmesReady = true;
        });
    },

    getLearnerProfile() {
      this.$logger.info('Getting learner profile', { studentId: this.meetingLearner.id }, true);
      this.learnerLineManagers = [];
      this.includeLineManager = false;
      this.$http.get(`/api/profile/users/${this.meetingLearner.id}`)
        .then(result => {
          this.learnerLineManagers = result.data.line_managers ?? [];
          if (!this.lineManagerForProgramme) {
            this.includeLineManager = false;
          }
        }).catch(error => {
          this.$logger.error('Error getting learner profile', { studentId: this.meetingLearner.id }, error);
          this.$ktoast.showError(error);
          this.learnerLineManagers = [];
          this.includeLineManager = false;
        });
    },
  },
};
</script>
